import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { getPlatformProductDetailAsync } from 'src/redux/actions/productActions'
import { getInventoryDetailsAsync } from 'src/redux/actions/omsProductActions'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import Loader from 'src/utils/Loader'
import PremiumBreadcrumb from 'src/app/components/navigation/PremiumBreadcrumb'
import { usePlatformBackUrl } from 'src/hooks/usePlatformBackUrl'
import { formatDateTime, timeAgo } from 'src/marketConnect/helpers'
import { DIMENSION_UNIT_OPTIONS, WEIGHT_UNIT_OPTIONS } from 'src/utils/constants'
import '../create-product/product.scss'
import { mapPlatformProductToReadOnlyView, NonOmsProductView } from './nonOmsProductDetailMapper'
import {
  countVariantMatrixColumns,
  getClassificationFieldVisibility,
  getPricingFieldVisibility,
  getShippingFieldVisibility,
  getVariantMatrixColumnVisibility,
  hasDisplayValue,
  hasRichTextDisplayValue,
  showClassificationSection,
  showDimensionUnitField,
  showPricingSection,
  showProductMediaSection,
  isMainStoreProductDetailView,
  showInventoryDetailsSection,
  showShippingSection,
  showVariantsAndOptionsSection,
} from './nonOmsProductDetailVisibility'
import { ReflowCardRow, ReflowHalfGrid, ReflowHalfItem } from './NonOmsProductDetailLayout'
import { NonOmsLinkingStatusSection } from './NonOmsLinkingStatusSection'
import { NonOmsProductLogsSection } from './NonOmsProductLogsSection'
import { useProductLinkingDetails } from './useProductLinkingDetails'

type InventoryLocation = {
  id: string
  name: string
  available: string
  reserved: string
  onHand: string
}

const readOnlyInputStyle = {
  backgroundColor: '#f5f8fa',
  color: '#3f4254',
  cursor: 'default',
}

const ReadOnlyField: FC<{
  label: string
  value?: string
  required?: boolean
  type?: string
}> = ({ label, value = '', required = false, type = 'text' }) => {
  if (!hasDisplayValue(value)) return null
  return (
    <div>
      <label className={`form-label${required ? ' required' : ''}`}>{label}</label>
      <input
        type={type}
        className="form-control"
        value={value}
        readOnly
        disabled
        style={readOnlyInputStyle}
      />
    </div>
  )
}

const PRODUCT_MEDIA_MAX_IMAGES = 50

const productMediaStyles = `
  @keyframes mp-pulse-ring {
    0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
    70% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
    100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
  }
  @keyframes mp-glow-pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.8; }
  }
  .mp-scroll-area::-webkit-scrollbar { width: 4px; }
  .mp-scroll-area::-webkit-scrollbar-track { background: transparent; }
  .mp-scroll-area::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.3); border-radius: 100px; }
  .mp-scroll-area::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.5); }
`

/** OMS-style product media gallery (read-only: prev/next + primary pin only). */
const NonOmsProductMediaSection: FC<{ images: string[] }> = ({ images }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const safeIndex = images.length === 0 ? 0 : Math.min(activeIndex, images.length - 1)
  const activeImage = images[safeIndex]
  const isPrimary = safeIndex === 0 && Boolean(activeImage)

  useEffect(() => {
    setActiveIndex(0)
  }, [images])

  const showPrevious = () => {
    if (images.length <= 1) return
    setActiveIndex((current) => (current <= 0 ? images.length - 1 : current - 1))
  }

  const showNext = () => {
    if (images.length <= 1) return
    setActiveIndex((current) => (current >= images.length - 1 ? 0 : current + 1))
  }

  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        borderRadius: 28,
        padding: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #667eea 100%)',
        backgroundSize: '300% 300%',
        boxSizing: 'border-box',
      }}
    >
      <style>{productMediaStyles}</style>
      <div
        style={{
          width: '100%',
          height: '34rem',
          background: '#ffffff',
          borderRadius: 26,
          padding: 20,
          boxSizing: 'border-box',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.12)',
        }}
      >
        <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', height: '100%' }}>
          <div style={{ flex: '0 0 70%', minWidth: 300, position: 'relative' }}>
            <div
              style={{
                width: '100%',
                height: '100%',
                borderRadius: 22,
                background: activeImage
                  ? 'linear-gradient(145deg, #0c1222 0%, #1a1a2e 50%, #16213e 100%)'
                  : '#f8fafc',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: activeImage
                  ? 'inset 0 2px 20px rgba(0,0,0,0.3), 0 8px 32px -8px rgba(0,0,0,0.3)'
                  : 'inset 0 0 0 1px #e2e8f0',
              }}
            >
              {activeImage ? (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      inset: -80,
                      backgroundImage: `url(${activeImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'blur(80px) saturate(1.8)',
                      opacity: 0.55,
                      transform: 'scale(1.3)',
                      animation: 'mp-glow-pulse 4s ease-in-out infinite',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 3,
                      pointerEvents: 'none',
                      background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)',
                    }}
                  />
                  <img
                    src={activeImage}
                    alt=""
                    style={{
                      maxWidth: '88%',
                      maxHeight: '88%',
                      objectFit: 'contain',
                      zIndex: 5,
                      filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.5))',
                      borderRadius: 8,
                    }}
                  />
                  {isPrimary && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        zIndex: 15,
                        background: 'rgba(99, 102, 241, 0.85)',
                        color: '#ffffff',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        padding: '5px 7px',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        border: '1px solid rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <span style={{ fontSize: 11 }}>★</span> PRIMARY
                    </div>
                  )}
                  {images.length > 1 && (
                    <button
                      type="button"
                      onClick={showPrevious}
                      aria-label="Previous image"
                      style={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        fontWeight: 'bold',
                        backdropFilter: 'blur(16px)',
                        zIndex: 15,
                      }}
                    >
                      ‹
                    </button>
                  )}
                  {images.length > 1 && (
                    <button
                      type="button"
                      onClick={showNext}
                      aria-label="Next image"
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22,
                        fontWeight: 'bold',
                        backdropFilter: 'blur(16px)',
                        zIndex: 15,
                      }}
                    >
                      ›
                    </button>
                  )}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 20,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      maxWidth: '70%',
                      background: 'rgba(0,0,0,0.45)',
                      padding: '8px 18px',
                      borderRadius: 100,
                      backdropFilter: 'blur(16px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      zIndex: 15,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                      {images.map((url, index) => (
                        <button
                          key={`dot-${url}-${index}`}
                          type="button"
                          aria-label={`Show image ${index + 1}`}
                          onClick={() => setActiveIndex(index)}
                          style={{
                            width: index === safeIndex ? 24 : 8,
                            height: 8,
                            padding: 0,
                            border: 'none',
                            borderRadius: 100,
                            flexShrink: 0,
                            background:
                              index === safeIndex
                                ? 'linear-gradient(90deg, #818cf8, #c084fc)'
                                : 'rgba(255,255,255,0.35)',
                            cursor: 'pointer',
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, marginLeft: 6 }}>
                      {safeIndex + 1}/{images.length}
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ color: '#64748b', fontSize: 15, fontWeight: 600 }}>No media</div>
              )}
            </div>
          </div>

          <div
            style={{
              flex: '0 0 30%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: '#f8fafc',
              borderRadius: 22,
              border: '1px solid #e2e8f0',
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e2e8f0',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Gallery</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#6366f1',
                  background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                  padding: '4px 12px',
                  borderRadius: 100,
                  border: '1px solid #c7d2fe',
                }}
              >
                {images.length} / {PRODUCT_MEDIA_MAX_IMAGES}
              </span>
            </div>
            <div className="mp-scroll-area" style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                {images.map((url, index) => {
                  const isActive = index === safeIndex
                  return (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      style={{
                        aspectRatio: '1/1',
                        borderRadius: 16,
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        background: '#fff',
                        padding: 0,
                        border: 'none',
                        boxShadow: isActive
                          ? '0 0 0 3px #fff, 0 0 0 5px #6366f1, 0 12px 28px -6px rgba(99,102,241,0.35)'
                          : '0 2px 8px -2px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.04)',
                        transform: isActive ? 'scale(0.95)' : 'scale(1)',
                        animation: isActive ? 'mp-pulse-ring 2s ease-in-out infinite' : undefined,
                      }}
                    >
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div
                        style={{
                          position: 'absolute',
                          left: 8,
                          top: 8,
                          background: isActive ? 'rgba(99, 102, 241, 0.9)' : 'rgba(255,255,255,0.85)',
                          color: isActive ? '#fff' : '#1e293b',
                          fontSize: 11,
                          fontWeight: 700,
                          width: 22,
                          height: 22,
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {index + 1}
                      </div>
                      {index === 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            background: 'rgba(99, 102, 241, 0.9)',
                            color: '#fff',
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                          }}
                        >
                          ★
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const NonOmsProductDetail: FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { variantId, platformId, productId, availablePlatformId } = params
  const { platformProductDetail, platformProductDetailLoader } = useAppSelector(
    (state) => state.productsOfSingleChannel
  )
  const { fetchedConnectedPlatformData, mainStoreData } = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const backUrl = usePlatformBackUrl(platformId)
  const [inventoryLocations, setInventoryLocations] = useState<InventoryLocation[]>([])
  const [inventorySearch, setInventorySearch] = useState('')
  const [inventoryLoading, setInventoryLoading] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const variantMatrixRef = useRef<HTMLDivElement>(null)

  const currentPlatformName = useMemo(() => {
    const platform = fetchedConnectedPlatformData?.find(
      (item: any) => Number(item?.id) === Number(platformId)
    )
    return (
      platform?.platform_info?.display_name ??
      platform?.display_name ??
      platform?.name ??
      'Products'
    )
  }, [fetchedConnectedPlatformData, platformId])

  const sourcePlatformName = useMemo(() => {
    return (
      mainStoreData?.display_name ??
      mainStoreData?.platform_info?.display_name ??
      mainStoreData?.name ??
      'source'
    )
  }, [mainStoreData])

  useEffect(() => {
    if (productId) {
      dispatch(getPlatformProductDetailAsync({ productId }))
    }
  }, [productId, dispatch])

  const view: NonOmsProductView = useMemo(() => {
    const loadedId = Number((platformProductDetail as any)?.id)
    if (!productId || loadedId !== Number(productId)) {
      return mapPlatformProductToReadOnlyView(null)
    }
    return mapPlatformProductToReadOnlyView(platformProductDetail)
  }, [platformProductDetail, productId])

  const selectedVariant = useMemo(() => {
    if (!view.variants.length) return undefined
    return view.variants.find((variant) => variant.id === String(variantId)) ?? view.variants[0]
  }, [view.variants, variantId])

  const rawSelectedVariant = useMemo(() => {
    const variants = (platformProductDetail as any)?.varients_data
    if (!Array.isArray(variants)) return undefined
    const id = variantId ?? selectedVariant?.id
    return variants.find((item: any) => String(item?.id) === String(id))
  }, [platformProductDetail, variantId, selectedVariant?.id])

  const { linkingRows, linkingContext } = useProductLinkingDetails({
    platformProductDetail,
    selectedVariant: rawSelectedVariant,
    variantId,
    platformId,
    mainStoreId: mainStoreData?.id,
    fetchedConnectedPlatformData: fetchedConnectedPlatformData || [],
  })

  const channelListingName = useMemo(() => {
    if (selectedVariant?.name && selectedVariant.name !== view.name) {
      return `${view.name} — ${selectedVariant.optionLabel || selectedVariant.name}`
    }
    return view.name || selectedVariant?.name || ''
  }, [view.name, selectedVariant])

  const isMainStoreView = isMainStoreProductDetailView(platformId, mainStoreData?.id)

  useEffect(() => {
    const inventoryVariantId = variantId || selectedVariant?.id
    if (!isMainStoreView || !inventoryVariantId || !platformId) {
      setInventoryLocations([])
      setInventoryLoading(false)
      return
    }

    let cancelled = false
    const loadInventory = async () => {
      setInventoryLoading(true)
      try {
        const response: any = await dispatch(
          getInventoryDetailsAsync({
            page: 1,
            limit: 10,
            sort_by: 'desc',
            order_by: 'id',
            variant_ids: String(inventoryVariantId),
            platform_id: Number(platformId),
          })
        )
        if (cancelled) return
        const rows = response?.payload?.results?.data || response?.payload?.data?.results?.data || []
        const match =
          (Array.isArray(rows) ? rows : []).find(
            (row: any) => String(row?.id) === String(inventoryVariantId)
          ) || rows?.[0]
        const locations = Array.isArray(match?.locations) ? match.locations : []
        setInventoryLocations(
          locations.map((location: any, index: number) => ({
            id: String(location?.location_id ?? index),
            name: location?.location_name || '',
            available:
              location?.available_quantity == null ? '' : String(location.available_quantity),
            reserved: location?.reserve_quantity == null ? '' : String(location.reserve_quantity),
            onHand: location?.on_hand == null ? '' : String(location.on_hand),
          }))
        )
      } catch {
        if (!cancelled) setInventoryLocations([])
      } finally {
        if (!cancelled) setInventoryLoading(false)
      }
    }

    loadInventory()
    return () => {
      cancelled = true
    }
  }, [variantId, selectedVariant?.id, platformId, dispatch, isMainStoreView])

  const filteredLocations = inventoryLocations.filter((location) =>
    location.name.toLowerCase().includes(inventorySearch.trim().toLowerCase())
  )
  const trackInventory = Boolean(selectedVariant?.isTracked || inventoryLocations.length > 0)

  const resolvedSku = selectedVariant?.sku || view.sku
  const resolvedBarcode = selectedVariant?.barcode || view.barcode
  const resolvedEan = selectedVariant?.ean || view.ean

  const classificationFields = useMemo(() => getClassificationFieldVisibility(view), [view])
  const showClassification = showClassificationSection(classificationFields)
  const pricingFields = useMemo(() => getPricingFieldVisibility(view), [view])
  const showPricing = showPricingSection(pricingFields)
  const shippingFields = useMemo(() => getShippingFieldVisibility(view), [view])
  const showShipping = showShippingSection(shippingFields)
  const showDimensionUnit = showDimensionUnitField(shippingFields)
  const showInventoryDetails = showInventoryDetailsSection(
    platformId,
    mainStoreData?.id,
    inventoryLoading,
    inventoryLocations.length
  )
  const showProductMedia = showProductMediaSection(view.images)
  const variantMatrixColumns = useMemo(
    () => getVariantMatrixColumnVisibility(view.variants),
    [view.variants]
  )
  const variantMatrixColumnCount = countVariantMatrixColumns(variantMatrixColumns)
  const showDetailedDescription = hasRichTextDisplayValue(view.description)
  const showVariantsAndOptions = showVariantsAndOptionsSection(view)
  const showDetailHalfCardRow =
    showClassification || showInventoryDetails || showPricing || showShipping

  const generalInfoPrimaryHalfItems = useMemo((): ReflowHalfItem[] => {
    const items: ReflowHalfItem[] = []
    if (hasDisplayValue(view.name)) {
      items.push({
        key: 'productName',
        content: (
          <>
            <ReadOnlyField label="Product Name" value={view.name} required />
            <div className="text-muted fs-7 mt-1">
              A product name is required and recommended to be unique.
            </div>
          </>
        ),
      })
    }
    if (hasDisplayValue(view.shortDescription)) {
      items.push({
        key: 'shortDescription',
        content: (
          <>
            <ReadOnlyField label="Short Description" value={view.shortDescription} />
            <div className="text-muted fs-7 mt-1">Brief summary shown in listings.</div>
          </>
        ),
      })
    }
    if (hasDisplayValue(resolvedSku)) {
      items.push({
        key: 'sku',
        content: <ReadOnlyField label="SKU" value={resolvedSku} required />,
      })
    }
    if (hasDisplayValue(resolvedBarcode)) {
      items.push({
        key: 'barcode',
        content: <ReadOnlyField label="Barcode" value={resolvedBarcode} />,
      })
    }
    if (hasDisplayValue(resolvedEan)) {
      items.push({
        key: 'ean',
        content: <ReadOnlyField label="EAN" value={resolvedEan} />,
      })
    }
    return items
  }, [view.name, view.shortDescription, resolvedSku, resolvedBarcode, resolvedEan])

  const generalInfoMetaHalfItems = useMemo((): ReflowHalfItem[] => {
    const items: ReflowHalfItem[] = []
    if (hasDisplayValue(view.createdAt)) {
      items.push({
        key: 'createdAt',
        content: (
          <>
            <div className="fw-semibold">Created At</div>
            <div className="text-muted fs-7 mt-1">{formatDateTime(view.createdAt)}</div>
          </>
        ),
      })
    }
    if (hasDisplayValue(view.updatedAt)) {
      items.push({
        key: 'updatedAt',
        content: (
          <>
            <div className="fw-semibold">Last Modified</div>
            <div className="text-muted fs-7 mt-1">{timeAgo(view.updatedAt)}</div>
          </>
        ),
      })
    }
    return items
  }, [view.createdAt, view.updatedAt])

  const pricingHalfItems = useMemo((): ReflowHalfItem[] => {
    const items: ReflowHalfItem[] = []
    if (pricingFields.salePrice) {
      items.push({
        key: 'salePrice',
        content: <ReadOnlyField label="Sale Price" value={view.salePrice} required />,
      })
    }
    if (pricingFields.costPrice) {
      items.push({
        key: 'costPrice',
        content: <ReadOnlyField label="Cost Price" value={view.costPrice} />,
      })
    }
    if (pricingFields.comparePrice) {
      items.push({
        key: 'comparePrice',
        content: <ReadOnlyField label="Compare Price" value={view.comparePrice} />,
      })
    }
    return items
  }, [pricingFields, view.salePrice, view.costPrice, view.comparePrice])

  const shippingDimensionHalfItems = useMemo((): ReflowHalfItem[] => {
    const items: ReflowHalfItem[] = []
    if (shippingFields.length) {
      items.push({
        key: 'length',
        content: <ReadOnlyField label="Length" value={view.length} />,
      })
    }
    if (shippingFields.width) {
      items.push({
        key: 'width',
        content: <ReadOnlyField label="Width" value={view.width} />,
      })
    }
    if (shippingFields.height) {
      items.push({
        key: 'height',
        content: <ReadOnlyField label="Height" value={view.height} />,
      })
    }
    if (showDimensionUnit) {
      items.push({
        key: 'dimensionUnit',
        content: (
          <>
            <label className="form-label">Unit</label>
            <select className="form-select" value={view.dimensionUnit} disabled style={readOnlyInputStyle}>
              {!DIMENSION_UNIT_OPTIONS.some((unit) => unit.value === view.dimensionUnit) && (
                <option value={view.dimensionUnit}>{view.dimensionUnit}</option>
              )}
              {DIMENSION_UNIT_OPTIONS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </>
        ),
      })
    }
    return items
  }, [
    shippingFields.length,
    shippingFields.width,
    shippingFields.height,
    showDimensionUnit,
    view.length,
    view.width,
    view.height,
    view.dimensionUnit,
  ])

  const descriptionHtml = DOMPurify.sanitize(view.description || '')

  useLayoutEffect(() => {
    const description = descriptionRef.current
    if (!description) return
    description.style.maxHeight = 'none'
    const fullHeight = description.scrollHeight
    description.style.maxHeight = `${Math.max(Math.round(fullHeight / 2), 120)}px`
    description.style.overflowY = 'auto'
  }, [descriptionHtml])

  useLayoutEffect(() => {
    const matrix = variantMatrixRef.current
    if (!matrix) return
    const header = matrix.querySelector('thead')
    const rows = matrix.querySelectorAll('tbody tr')
    if (rows.length <= 5) {
      matrix.style.maxHeight = 'none'
      matrix.style.overflowY = 'visible'
      return
    }
    const headerHeight = header?.getBoundingClientRect().height ?? 0
    let rowsHeight = 0
    for (let index = 0; index < 5; index += 1) {
      rowsHeight += rows[index].getBoundingClientRect().height
    }
    matrix.style.maxHeight = `${Math.ceil(headerHeight + rowsHeight)}px`
    matrix.style.overflowY = 'auto'
  }, [view.variants, variantMatrixColumnCount])
  const listingUrl =
    availablePlatformId && platformId
      ? `/listing-product-table/${availablePlatformId}/${platformId}`
      : backUrl

  if (platformProductDetailLoader && !view.name) return <Loader />

  return (
    <div className="variantScope page page--create-product page--non-oms-product-detail d-flex flex-column">
      <div className="variantScope__scroll">
        <div className="variantScope__header">
          <div className="d-flex align-items-center w-100 px-4">
            <div className="d-flex flex-column">
              <PremiumBreadcrumb
                items={[
                  { label: currentPlatformName, to: listingUrl },
                  { label: 'Product Details' },
                ]}
              />
              <span className="fw-bold" style={{ fontSize: 16 }}>
                Product Details
              </span>
            </div>
            <div className="ms-auto">
              <button
                type="button"
                className="btn btn-sm btn-light fw-bold"
                onClick={() => navigate(listingUrl)}
              >
                Back
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 pb-6">
          <div className="d-flex flex-column gap-4">
            <div className="card card-flush">
              <div className="card-headerP">
                <div className="card-titleP">
                  <h2>General Information</h2>
                </div>
              </div>
              <div className="card-bodyP pt-0 d-flex flex-column gap-4">
                {generalInfoPrimaryHalfItems.length > 0 ? (
                  <ReflowHalfGrid items={generalInfoPrimaryHalfItems} />
                ) : null}

                {showDetailedDescription ? (
                  <div>
                    <label className="form-label">Detailed Description</label>
                    <div
                      ref={descriptionRef}
                      className="form-control"
                      style={{ ...readOnlyInputStyle, minHeight: 120, height: 'auto', overflowY: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    />
                  </div>
                ) : null}

                {generalInfoMetaHalfItems.length > 0 ? (
                  <ReflowHalfGrid items={generalInfoMetaHalfItems} />
                ) : null}
              </div>
            </div>

            <NonOmsLinkingStatusSection
              channelListingName={channelListingName}
              channelListingSku={selectedVariant?.sku || view.sku}
              channelPlatformName={currentPlatformName}
              linkingRows={linkingRows}
              linkingContext={linkingContext}
              fetchedConnectedPlatformData={fetchedConnectedPlatformData || []}
              platformId={platformId}
              availablePlatformId={availablePlatformId}
              hasVariantsData={Array.isArray((platformProductDetail as any)?.varients_data)}
              sourcePlatformName={sourcePlatformName}
            />

            {showVariantsAndOptions ? (
            <div className="card card-flush">
              <div className="card-headerP d-flex justify-content-between align-items-center">
                <div className="card-titleP">
                  <h2 className="mb-0">Variants & Options</h2>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="form-check-label text-muted mb-0" style={{ fontSize: 13 }}>
                    This product has variants
                  </label>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input h-20px w-40px"
                      type="checkbox"
                      checked={view.hasVariants}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
              {view.hasVariants && (
                <div className="card-bodyP pt-0 mt-2">
                  <div className="border rounded">
                    <div className="card-titleP p-3 border-bottom">
                      <h2 className="mb-0">Active Options</h2>
                    </div>
                    {view.options.length === 0 ? (
                      <div className="px-3 py-3 text-muted">No options</div>
                    ) : (
                      view.options.map((option) => (
                        <div
                          key={option.name}
                          className="d-flex align-items-center px-3 py-3 border-bottom"
                          style={{ gap: 16 }}
                        >
                          <div style={{ minWidth: 120, fontWeight: 700 }}>{option.name || ''}</div>
                          <div className="d-flex gap-2 flex-wrap">
                            {option.values.map((value) => (
                              <span key={value} className="badge bg-light text-dark px-3 py-2">
                                {value}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="card mt-3 mb-3">
                    <div className="p-3 border-bottom">
                      <div className="card-titleP d-flex align-items-center justify-content-between">
                        <h2 className="mb-0">Variant Matrix</h2>
                        <span className="badge bg-light text-dark">
                          {view.variants.length} {view.variants.length === 1 ? 'variant' : 'variants'}
                        </span>
                      </div>
                    </div>
                    <div ref={variantMatrixRef} className="table-responsive" style={{ padding: 10 }}>
                      <table className="table align-middle mb-0">
                        <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa' }}>
                          <tr>
                            {variantMatrixColumns.media ? (
                              <th className="text-start" style={{ width: 90 }}>
                                Media
                              </th>
                            ) : null}
                            <th className="text-start" style={{ width: 180 }}>
                              Variant
                            </th>
                            {variantMatrixColumns.salePrice ? (
                              <th className="text-center">Sale Price</th>
                            ) : null}
                            {variantMatrixColumns.costPrice ? (
                              <th className="text-center">Cost Price</th>
                            ) : null}
                            {variantMatrixColumns.available ? (
                              <th className="text-center">Available</th>
                            ) : null}
                            {variantMatrixColumns.sku ? <th className="text-center">SKU</th> : null}
                            {variantMatrixColumns.barcode ? (
                              <th className="text-center">Barcode</th>
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {view.variants.length === 0 ? (
                            <tr>
                              <td colSpan={variantMatrixColumnCount} className="text-center text-muted py-4">
                                No variants
                              </td>
                            </tr>
                          ) : (
                            view.variants.map((variant) => (
                              <tr key={variant.id}>
                                {variantMatrixColumns.media ? (
                                  <td>
                                    <div
                                      className="border rounded d-flex align-items-center justify-content-center"
                                      style={{ width: 40, height: 40, background: '#fff' }}
                                    >
                                      {variant.imageUrl ? (
                                        <img
                                          src={variant.imageUrl}
                                          alt=""
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 6,
                                          }}
                                        />
                                      ) : null}
                                    </div>
                                  </td>
                                ) : null}
                                <td>
                                  <div className="d-flex flex-column align-items-start gap-1">
                                    <span className="fw-semibold">{variant.optionLabel}</span>
                                    {variant.name && (
                                      <span
                                        className="badge"
                                        style={{
                                          fontSize: 11,
                                          padding: '4px 8px',
                                          background: '#f1f1f4',
                                          color: '#5e6278',
                                          border: '1px solid #e4e6ef',
                                          borderRadius: 6,
                                        }}
                                      >
                                        {variant.name}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                {variantMatrixColumns.salePrice ? (
                                  <td>
                                    <input
                                      className="form-control"
                                      value={variant.salePrice}
                                      readOnly
                                      disabled
                                      style={readOnlyInputStyle}
                                    />
                                  </td>
                                ) : null}
                                {variantMatrixColumns.costPrice ? (
                                  <td>
                                    <input
                                      className="form-control"
                                      value={variant.costPrice}
                                      readOnly
                                      disabled
                                      style={readOnlyInputStyle}
                                    />
                                  </td>
                                ) : null}
                                {variantMatrixColumns.available ? (
                                  <td>
                                    <input
                                      className="form-control"
                                      value={variant.available}
                                      readOnly
                                      disabled
                                      style={readOnlyInputStyle}
                                    />
                                  </td>
                                ) : null}
                                {variantMatrixColumns.sku ? (
                                  <td>
                                    <input
                                      className="form-control"
                                      value={variant.sku}
                                      readOnly
                                      disabled
                                      style={readOnlyInputStyle}
                                    />
                                  </td>
                                ) : null}
                                {variantMatrixColumns.barcode ? (
                                  <td>
                                    <input
                                      className="form-control"
                                      value={variant.barcode}
                                      readOnly
                                      disabled
                                      style={readOnlyInputStyle}
                                    />
                                  </td>
                                ) : null}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
            ) : null}

            {showProductMedia ? (
              <div className="card card-flush">
                <div className="card-headerP">
                  <div className="card-titleP">
                    <h2>Product Media</h2>
                  </div>
                </div>
                <div className="card-bodyP pt-0">
                  <NonOmsProductMediaSection images={view.images} />
                </div>
              </div>
            ) : null}

            {showDetailHalfCardRow ? (
            <ReflowCardRow>
              {showClassification ? (
                <div className="col-12 col-lg-6 d-flex">
                  <div className="card card-flush w-100 h-100">
                    <div className="card-headerP">
                      <div className="card-titleP">
                        <h2>Classification</h2>
                      </div>
                    </div>
                    <div className="card-bodyP pt-0">
                      {classificationFields.category ? (
                        <ReadOnlyField label="Category" value={view.category} />
                      ) : null}
                      {classificationFields.type ? (
                        <div className={classificationFields.category ? 'mt-3' : undefined}>
                          <ReadOnlyField label="Type" value={view.type} required />
                        </div>
                      ) : null}
                      {classificationFields.vendor ? (
                        <div
                          className={
                            classificationFields.category || classificationFields.type ? 'mt-3' : undefined
                          }
                        >
                          <ReadOnlyField label="Vendor" value={view.vendor} />
                        </div>
                      ) : null}
                      {classificationFields.tags ? (
                        <div
                          className={
                            classificationFields.category ||
                            classificationFields.type ||
                            classificationFields.vendor
                              ? 'mt-3'
                              : undefined
                          }
                        >
                          <label className="form-label d-block">Tags</label>
                          <div
                            className="form-control non-oms-tags-field"
                            style={readOnlyInputStyle}
                          >
                            {view.tags.map((tag) => (
                              <span key={tag} className="badge bg-light text-dark">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {classificationFields.status ? (
                        <div
                          className={
                            classificationFields.category ||
                            classificationFields.type ||
                            classificationFields.vendor ||
                            classificationFields.tags
                              ? 'mt-3'
                              : undefined
                          }
                        >
                          <ReadOnlyField label="Product Status" value={view.status} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {showInventoryDetails ? (
              <div className="col-12 col-lg-6 d-flex">
                <div className="card card-flush w-100 h-100">
                  <div className="card-headerP d-flex justify-content-between align-items-center">
                    <div className="card-titleP">
                      <h2 className="mb-0">Inventory Details</h2>
                    </div>
                    <div className="mx-3 flex-grow-1" style={{ maxWidth: 280 }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search inventory location..."
                        value={inventorySearch}
                        onChange={(event) => setInventorySearch(event.target.value)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-check-label text-muted mb-0" style={{ fontSize: 13 }}>
                        Track Inventory
                      </label>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input h-20px w-40px"
                          type="checkbox"
                          checked={trackInventory}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-bodyP pt-0 mt-2">
                    {inventoryLoading ? (
                      <div className="text-muted fs-7 py-4 text-center">Loading inventory...</div>
                    ) : (
                      <div className="border rounded" style={{ maxHeight: 395, overflowY: 'auto' }}>
                        <table className="table table-row-dashed table-row-gray-300 align-middle gs-3 gy-3 mb-0">
                          <thead className="bg-light sticky-top">
                            <tr className="fw-bold text-gray-800 border-bottom">
                              <th className="min-w-150px ps-4">Warehouse</th>
                              <th className="text-center">Available</th>
                              <th className="text-center">Reserved</th>
                              <th className="text-center pe-4">On Hand</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLocations.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-4">
                                  No locations found
                                </td>
                              </tr>
                            ) : (
                              filteredLocations.map((location) => (
                                <tr key={location.id}>
                                  <td className="ps-4 fw-semibold text-gray-700">{location.name}</td>
                                  <td className="text-center">
                                    <input
                                      className="form-control text-center mx-auto"
                                      value={location.available}
                                      readOnly
                                      disabled
                                      style={{ ...readOnlyInputStyle, maxWidth: 100 }}
                                    />
                                  </td>
                                  <td className="text-center text-gray-600 fw-semibold">{location.reserved}</td>
                                  <td className="text-center text-gray-600 fw-semibold pe-4">{location.onHand}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              ) : null}

              {showPricing ? (
              <div className="col-12 col-lg-6 d-flex">
                <div className="card card-flush w-100 h-100">
                  <div className="card-headerP">
                    <div className="card-titleP">
                      <h2>Pricing</h2>
                    </div>
                  </div>
                  <div className="card-bodyP pt-0 mt-2">
                    <ReflowHalfGrid gapClass="g-3" items={pricingHalfItems} />
                  </div>
                </div>
              </div>
              ) : null}

              {showShipping ? (
              <div className="col-12 col-lg-6 d-flex">
                <div className="card card-flush w-100 h-100">
                  <div className="card-headerP">
                    <div className="card-titleP">
                      <h2>Shipping & Dimensions</h2>
                    </div>
                  </div>
                  <div className="card-bodyP pt-0 mt-2">
                    {shippingFields.weight ? (
                      <div
                        className={`row g-3 align-items-end${
                          shippingFields.length || shippingFields.width || shippingFields.height
                            ? ' mb-4'
                            : ''
                        }`}
                      >
                        <div className="col-12">
                          <label className="form-label">Product weight</label>
                          <div className="d-flex gap-3">
                            <input
                              className="form-control"
                              value={view.weight}
                              readOnly
                              disabled
                              style={readOnlyInputStyle}
                            />
                            <select
                              className="form-select"
                              value={view.weightUnit}
                              disabled
                              style={{ ...readOnlyInputStyle, maxWidth: 110 }}
                            >
                              {!WEIGHT_UNIT_OPTIONS.some((unit) => unit.value === view.weightUnit) && (
                                <option value={view.weightUnit}>{view.weightUnit}</option>
                              )}
                              {WEIGHT_UNIT_OPTIONS.map((unit) => (
                                <option key={unit.value} value={unit.value}>
                                  {unit.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <ReflowHalfGrid
                      gapClass="g-3"
                      columnClassName="col-12 col-md-6 col-lg-3"
                      items={shippingDimensionHalfItems}
                    />
                  </div>
                </div>
              </div>
              ) : null}
            </ReflowCardRow>
            ) : null}

            <NonOmsProductLogsSection variantId={selectedVariant?.id || variantId} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default NonOmsProductDetail
